import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { formatMoney, formatPercent, formatTimeAgo } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { TrendingUp, TrendingDown, Bot, Settings, BarChart3, RefreshCw } from 'lucide-react';

interface BotSettings {
    enabled: boolean;
    minScore: number;
    defaultSize: number;
    maxOpenTrades: number;
}

interface BotStats {
    totalTrades: number;
    openTrades: number;
    resolvedTrades: number;
    totalPnl: number;
    winCount: number;
    lossCount: number;
    winRate: number;
    avgPnlPerTrade: number;
    bestTrade: number;
    worstTrade: number;
}

interface PaperTrade {
    id: string;
    alertId: string;
    marketId: string;
    walletAddress: string;
    side: 'YES' | 'NO';
    entryPrice: number;
    size: number;
    status: 'open' | 'resolved';
    exitPrice: number | null;
    pnl: number | null;
    score: number;
    createdAt: number;
    resolvedAt: number | null;
}

export function TradeBot() {
    const [settings, setSettings] = useState<BotSettings | null>(null);
    const [stats, setStats] = useState<BotStats | null>(null);
    const [trades, setTrades] = useState<PaperTrade[]>([]);
    const [breakdown, setBreakdown] = useState<Array<{ minScore: number; stats: BotStats }>>([]);
    const [loading, setLoading] = useState(true);
    const [tradeFilter, setTradeFilter] = useState<'open' | 'resolved' | 'all'>('all');
    const [savingSettings, setSavingSettings] = useState(false);

    // Local settings form state
    const [formSettings, setFormSettings] = useState<BotSettings>({
        enabled: true,
        minScore: 60,
        defaultSize: 100,
        maxOpenTrades: 50,
    });

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        fetchTrades();
    }, [tradeFilter]);

    async function fetchData() {
        setLoading(true);
        try {
            const [settingsRes, statsRes, breakdownRes] = await Promise.all([
                api.get<{ success: boolean; data: BotSettings }>('/api/tradebot/settings'),
                api.get<{ success: boolean; data: BotStats }>('/api/tradebot/stats'),
                api.get<{ success: boolean; data: Array<{ minScore: number; stats: BotStats }> }>(
                    '/api/tradebot/stats/breakdown'
                ),
            ]);

            if (settingsRes.success) {
                setSettings(settingsRes.data);
                setFormSettings(settingsRes.data);
            }
            if (statsRes.success) setStats(statsRes.data);
            if (breakdownRes.success) setBreakdown(breakdownRes.data);
        } catch (err) {
            console.error('Failed to fetch bot data:', err);
        } finally {
            setLoading(false);
        }
    }

    async function fetchTrades() {
        try {
            const params = new URLSearchParams();
            if (tradeFilter !== 'all') params.set('status', tradeFilter);
            params.set('limit', '50');

            const res = await api.get<{ success: boolean; data: { trades: PaperTrade[]; total: number } }>(
                `/api/tradebot/trades?${params}`
            );
            if (res.success) setTrades(res.data.trades);
        } catch (err) {
            console.error('Failed to fetch trades:', err);
        }
    }

    async function saveSettings() {
        setSavingSettings(true);
        try {
            const res = await api.put<{ success: boolean; data: BotSettings }>(
                '/api/tradebot/settings',
                formSettings
            );
            if (res.success) setSettings(res.data);
        } catch (err) {
            console.error('Failed to save settings:', err);
        } finally {
            setSavingSettings(false);
        }
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <Bot className="w-7 h-7 text-primary" />
                        Trade Bot
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Paper trading based on whale alerts
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Badge variant={settings?.enabled ? 'default' : 'secondary'} className="text-sm">
                        {settings?.enabled ? '🟢 Active' : '⏸️ Paused'}
                    </Badge>
                    <Button variant="ghost" size="icon" onClick={fetchData}>
                        <RefreshCw className="w-4 h-4" />
                    </Button>
                </div>
            </div>

            {/* Stats Overview */}
            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    <StatCard
                        label="Total PnL"
                        value={formatMoney(stats.totalPnl)}
                        className={stats.totalPnl >= 0 ? 'text-green-400' : 'text-red-400'}
                        icon={stats.totalPnl >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                    />
                    <StatCard label="Total Trades" value={stats.totalTrades.toString()} />
                    <StatCard label="Open" value={stats.openTrades.toString()} />
                    <StatCard label="Win Rate" value={formatPercent(stats.winRate)} />
                    <StatCard
                        label="Best Trade"
                        value={formatMoney(stats.bestTrade)}
                        className="text-green-400"
                    />
                    <StatCard
                        label="Worst Trade"
                        value={formatMoney(stats.worstTrade)}
                        className="text-red-400"
                    />
                </div>
            )}

            {/* Tabs */}
            <Tabs defaultValue="performance" className="space-y-4">
                <TabsList>
                    <TabsTrigger value="performance" className="gap-2">
                        <BarChart3 className="w-4 h-4" />
                        Performance
                    </TabsTrigger>
                    <TabsTrigger value="trades" className="gap-2">
                        <TrendingUp className="w-4 h-4" />
                        Trades
                    </TabsTrigger>
                    <TabsTrigger value="settings" className="gap-2">
                        <Settings className="w-4 h-4" />
                        Settings
                    </TabsTrigger>
                </TabsList>

                {/* Performance Tab */}
                <TabsContent value="performance">
                    <Card>
                        <CardHeader>
                            <CardTitle>Performance by Score Threshold</CardTitle>
                            <CardDescription>
                                What would your PnL be if you only took trades above each score threshold?
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Min Score</TableHead>
                                        <TableHead className="text-center">Trades</TableHead>
                                        <TableHead className="text-center">Win Rate</TableHead>
                                        <TableHead className="text-right">Total PnL</TableHead>
                                        <TableHead className="text-right">Avg PnL/Trade</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {breakdown.map((row) => (
                                        <TableRow key={row.minScore}>
                                            <TableCell>
                                                <Badge variant="secondary" className="font-mono">
                                                    {row.minScore}+
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-center">{row.stats.resolvedTrades}</TableCell>
                                            <TableCell className="text-center font-mono">
                                                {formatPercent(row.stats.winRate)}
                                            </TableCell>
                                            <TableCell
                                                className={`text-right font-mono font-medium ${row.stats.totalPnl >= 0 ? 'text-green-400' : 'text-red-400'
                                                    }`}
                                            >
                                                {row.stats.totalPnl >= 0 ? '+' : ''}
                                                {formatMoney(row.stats.totalPnl)}
                                            </TableCell>
                                            <TableCell
                                                className={`text-right font-mono ${row.stats.avgPnlPerTrade >= 0 ? 'text-green-400' : 'text-red-400'
                                                    }`}
                                            >
                                                {formatMoney(row.stats.avgPnlPerTrade)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Trades Tab */}
                <TabsContent value="trades">
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between">
                            <CardTitle>Paper Trades</CardTitle>
                            <Select value={tradeFilter} onValueChange={(v) => setTradeFilter(v as typeof tradeFilter)}>
                                <SelectTrigger className="w-[140px]">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Trades</SelectItem>
                                    <SelectItem value="open">Open</SelectItem>
                                    <SelectItem value="resolved">Resolved</SelectItem>
                                </SelectContent>
                            </Select>
                        </CardHeader>
                        <CardContent className="p-0">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Time</TableHead>
                                        <TableHead>Score</TableHead>
                                        <TableHead>Side</TableHead>
                                        <TableHead>Entry</TableHead>
                                        <TableHead>Size</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead className="text-right">PnL</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {trades.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                                                No trades yet
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        trades.map((trade) => (
                                            <TableRow key={trade.id}>
                                                <TableCell className="text-muted-foreground text-sm">
                                                    {formatTimeAgo(trade.createdAt)}
                                                </TableCell>
                                                <TableCell>
                                                    <Badge variant="secondary" className="font-mono">
                                                        {trade.score}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell>
                                                    <span
                                                        className={`font-semibold ${trade.side === 'YES' ? 'text-green-400' : 'text-red-400'
                                                            }`}
                                                    >
                                                        {trade.side}
                                                    </span>
                                                </TableCell>
                                                <TableCell className="font-mono">
                                                    {(trade.entryPrice * 100).toFixed(0)}¢
                                                </TableCell>
                                                <TableCell className="font-mono">{formatMoney(trade.size)}</TableCell>
                                                <TableCell>
                                                    <Badge variant={trade.status === 'open' ? 'outline' : 'secondary'}>
                                                        {trade.status}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell
                                                    className={`text-right font-mono font-medium ${trade.pnl === null
                                                            ? 'text-muted-foreground'
                                                            : trade.pnl >= 0
                                                                ? 'text-green-400'
                                                                : 'text-red-400'
                                                        }`}
                                                >
                                                    {trade.pnl === null ? '-' : `${trade.pnl >= 0 ? '+' : ''}${formatMoney(trade.pnl)}`}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Settings Tab */}
                <TabsContent value="settings">
                    <Card>
                        <CardHeader>
                            <CardTitle>Bot Settings</CardTitle>
                            <CardDescription>
                                Configure the paper trading bot's behavior
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                                <div>
                                    <div className="font-medium">Bot Status</div>
                                    <div className="text-sm text-muted-foreground">
                                        Enable or disable automatic paper trading
                                    </div>
                                </div>
                                <Button
                                    variant={formSettings.enabled ? 'default' : 'secondary'}
                                    onClick={() => setFormSettings({ ...formSettings, enabled: !formSettings.enabled })}
                                >
                                    {formSettings.enabled ? 'Enabled' : 'Disabled'}
                                </Button>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium">Minimum Score</label>
                                    <Input
                                        type="number"
                                        value={formSettings.minScore}
                                        onChange={(e) =>
                                            setFormSettings({ ...formSettings, minScore: parseInt(e.target.value) || 0 })
                                        }
                                        min={0}
                                        max={100}
                                    />
                                    <p className="text-xs text-muted-foreground">
                                        Only take trades from alerts with this score or higher
                                    </p>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-sm font-medium">Default Position Size</label>
                                    <Input
                                        type="number"
                                        value={formSettings.defaultSize}
                                        onChange={(e) =>
                                            setFormSettings({ ...formSettings, defaultSize: parseInt(e.target.value) || 0 })
                                        }
                                        min={1}
                                    />
                                    <p className="text-xs text-muted-foreground">USD per trade</p>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-sm font-medium">Max Open Trades</label>
                                    <Input
                                        type="number"
                                        value={formSettings.maxOpenTrades}
                                        onChange={(e) =>
                                            setFormSettings({
                                                ...formSettings,
                                                maxOpenTrades: parseInt(e.target.value) || 0,
                                            })
                                        }
                                        min={1}
                                    />
                                    <p className="text-xs text-muted-foreground">
                                        Maximum concurrent open positions
                                    </p>
                                </div>
                            </div>

                            <Button onClick={saveSettings} disabled={savingSettings}>
                                {savingSettings ? 'Saving...' : 'Save Settings'}
                            </Button>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
}

function StatCard({
    label,
    value,
    className,
    icon,
}: {
    label: string;
    value: string;
    className?: string;
    icon?: React.ReactNode;
}) {
    return (
        <Card>
            <CardContent className="p-4">
                <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-muted-foreground uppercase tracking-wide">{label}</span>
                    {icon}
                </div>
                <div className={`text-xl font-bold font-mono ${className || ''}`}>{value}</div>
            </CardContent>
        </Card>
    );
}
